import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import StateTransitionListComponent from './StateTransitionListComponent';
import { loadStateTransitions } from '../actionCreators';
import { getStateTransitions } from '../selectors';
import { RightsContext } from 'providers/RightsContext';
import debounce from 'lodash.debounce';
import { getOfferType } from 'features/offer/edit/client/form/selectors';
import { useDispatch, useSelector } from 'react-redux';
import documentTypeIds from 'constants/documentTypeIds';
import WrappedFakeText from 'components/common/WrappedFakeText';

const StateTransitionsContainer = ({ documentId, siteId, documentTypeId }) => {
  const DEBOUNCE_DELAY = 50;
  const dispatch = useDispatch();

  const { isImage, otherStateTransitionHistoryRead } = useContext(RightsContext);
  const [localStateTransitions, setLocalStateTransitions] = useState([]);
  const [fullTextFilter, setFullTextFilter] = useState('');

  const stateTransitions = useSelector(state => getStateTransitions(state, `${documentId}-${siteId}`));
  const offerTypeId = useSelector(state => getOfferType(state));

  useEffect(() => {
    dispatch(loadStateTransitions(documentId, siteId));
  }, [documentId, siteId, dispatch]);

  useEffect(() => {
    if (stateTransitions.loading || stateTransitions.loaded) return;
    dispatch(loadStateTransitions(documentId, siteId));
  }, [documentId, siteId, stateTransitions.loading, stateTransitions.loaded, dispatch]);

  useEffect(() => {
    if (stateTransitions.loading) return;
    setLocalStateTransitions(stateTransitions.items);
  }, [stateTransitions.loading, stateTransitions.items]);

  const filterStateTransitions = (items, filter) => {
    setFullTextFilter(filter);
    return filter !== '' ? filterByValue(items, filter) : items;
  };

  const filterByValue = (items, filter) => {
    const result = items.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && (k === 'documentStateName' || k === 'comment' || k === 'reason'))
          return o[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
    return result;
  };

  const handleFilterValueChangeDebounce = filter => {
    setLocalStateTransitions(filterStateTransitions(stateTransitions.items, filter));
  };

  const filterChangedDebounced = debounce(handleFilterValueChangeDebounce, DEBOUNCE_DELAY);

  const handleFilterValueChanged = (value, debounced) => {
    if (debounced) filterChangedDebounced(value);
  };

  const resetFilters = () => {
    setLocalStateTransitions(filterStateTransitions(stateTransitions.items, ''));
    setFullTextFilter('');
  };

  if (isImage || (documentTypeIds.Offer === documentTypeId && !otherStateTransitionHistoryRead)) return <></>;
  if (stateTransitions.loading) return <WrappedFakeText lines={7} onPaper />;

  return (
    <StateTransitionListComponent
      stateTransitions={localStateTransitions}
      onFilterValueChanged={handleFilterValueChanged}
      fullTextFilter={fullTextFilter}
      resetFilters={resetFilters}
      offerTypeId={offerTypeId}
      documentTypeId={documentTypeId}
    />
  );
};

StateTransitionsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documentTypeId: PropTypes.number.isRequired
};

export default StateTransitionsContainer;
